import { useEffect, useMemo, useState } from "react";
import PageShell from "@/components/PageShell";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CaretLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { CONTINUOUS_DYNAMICS } from "@/components/method/continuousDynamics";

/**
 * Reflection Balance — a quiet, non-diagnostic view of the advisor's own
 * reflective activity across the two continuous dynamics.
 *
 * Copy governance:
 *   • No percentages, ratios, deltas, red/green treatment, or trend arrows.
 *   • No cross-advisor comparisons. Endpoint is per-partner-only.
 *   • "Areas with no captured reflection" — never called gaps, blind spots,
 *     weaknesses, or missing.
 *   • Explanatory subtitles avoid imperative language ("you should…").
 */
function Sparkline({ data, dynamicKey }) {
  // Simple SVG sparkline — 14 daily counts for one dynamic. Kept minimal so
  // the page reads as a summary, not a dashboard.
  const values = data.map((d) => d[dynamicKey] || 0);
  const max = Math.max(1, ...values);
  const width = 280;
  const height = 32;
  const step = width / Math.max(1, values.length - 1);
  const points = values
    .map((v, i) => `${i * step},${height - (v / max) * (height - 4) - 2}`)
    .join(" ");
  return (
    <svg width={width} height={height} className="text-brass">
      <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={points} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function ReflectionBalance() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reflection-balance").then((r) => setData(r.data)); }, []);

  if (!data) {
    return <div className="p-8 text-muted-foreground text-sm">Loading your reflection balance…</div>;
  }

  const { totals, by_client, by_engagement, recent_activity, unreflected_scope } = data;
  const connections = CONTINUOUS_DYNAMICS.connections;
  const leadership = CONTINUOUS_DYNAMICS.leadership;
  const combined = (totals.connections || 0) + (totals.leadership || 0);

  return (
    <PageShell size="read" testid="reflection-balance-page">
      <Link to="/method" className="inline-flex items-center gap-1 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary" data-testid="reflection-balance-back">
        <CaretLeft size={11} weight="bold" /> Back to method
      </Link>
      <div className="mt-4">
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.24em] text-primary">Reflection Balance</div>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">A quiet view of your reflective activity</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed" style={{ fontFamily: "Georgia, serif", fontStyle: "italic" }}>
          Patterns are for your interpretation. Silence isn&rsquo;t judgment — some engagements simply haven&rsquo;t asked for one of these lenses yet.
        </p>
      </div>

      {/* Overall */}
      <div className="mt-8 border border-border bg-card p-6" data-testid="rb-overall">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">Overall</div>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-b border-border">
              <td className="py-2" style={{ fontFamily: "Georgia, serif", fontStyle: "italic" }}>{connections.shortLabel}</td>
              <td className="py-2 text-right font-mono tabular-nums" data-testid="rb-total-connections">{totals.connections || 0}</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2" style={{ fontFamily: "Georgia, serif", fontStyle: "italic" }}>{leadership.shortLabel}</td>
              <td className="py-2 text-right font-mono tabular-nums" data-testid="rb-total-leadership">{totals.leadership || 0}</td>
            </tr>
            <tr>
              <td className="pt-3 text-[0.6875rem] font-mono uppercase tracking-widest text-muted-foreground">Combined</td>
              <td className="pt-3 text-right font-mono tabular-nums font-bold" data-testid="rb-total-combined">{combined}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Recent activity */}
      <div className="mt-6 border border-border bg-card p-6" data-testid="rb-recent-activity">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">Recent activity · last 14 days</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2">{connections.shortLabel}</div>
            <Sparkline data={recent_activity} dynamicKey="connections" />
          </div>
          <div>
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-2">{leadership.shortLabel}</div>
            <Sparkline data={recent_activity} dynamicKey="leadership" />
          </div>
        </div>
      </div>

      {/* By client */}
      {by_client.length > 0 && (
        <div className="mt-6 border border-border bg-card p-6" data-testid="rb-by-client">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">By client</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                <th className="text-left py-1 font-normal">Partner Organization</th>
                <th className="text-right py-1 font-normal">C</th>
                <th className="text-right py-1 font-normal">L</th>
              </tr>
            </thead>
            <tbody>
              {by_client.map((r) => (
                <tr key={r.client_id} className="border-t border-border">
                  <td className="py-2">
                    <Link to={`/clients/${r.client_id}`} className="hover:text-brass">{r.client_name}</Link>
                  </td>
                  <td className="py-2 text-right font-mono tabular-nums">{r.connections}</td>
                  <td className="py-2 text-right font-mono tabular-nums">{r.leadership}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* By engagement */}
      {by_engagement.length > 0 && (
        <div className="mt-6 border border-border bg-card p-6" data-testid="rb-by-engagement">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">By engagement</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                <th className="text-left py-1 font-normal">Engagement</th>
                <th className="text-right py-1 font-normal">C</th>
                <th className="text-right py-1 font-normal">L</th>
              </tr>
            </thead>
            <tbody>
              {by_engagement.map((r) => (
                <tr key={r.engagement_id} className="border-t border-border">
                  <td className="py-2">
                    <Link to={`/engagements/${r.engagement_id}`} className="hover:text-brass">{r.engagement_name}</Link>
                  </td>
                  <td className="py-2 text-right font-mono tabular-nums">{r.connections}</td>
                  <td className="py-2 text-right font-mono tabular-nums">{r.leadership}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Areas with no captured reflection — neutrally framed */}
      {(unreflected_scope.clients.length > 0 || unreflected_scope.engagements.length > 0) && (
        <div className="mt-6 border border-border bg-card p-6" data-testid="rb-unreflected">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Areas with no captured reflection</div>
          <p className="text-[0.75rem] italic text-muted-foreground leading-relaxed mb-4" style={{ fontFamily: "Georgia, serif" }}>
            This list surfaces scope where YadidOS has no recorded reflection yet. It does not indicate that thinking is missing — only that nothing has been captured.
          </p>
          {unreflected_scope.clients.length > 0 && (
            <div className="mb-3">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-1">Partner Organizations</div>
              <ul className="space-y-1">
                {unreflected_scope.clients.map((c) => (
                  <li key={c.client_id} className="text-sm">
                    <Link to={`/clients/${c.client_id}`} className="hover:text-brass">{c.client_name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {unreflected_scope.engagements.length > 0 && (
            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary mb-1">Engagements</div>
              <ul className="space-y-1">
                {unreflected_scope.engagements.map((e) => (
                  <li key={e.engagement_id} className="text-sm">
                    <Link to={`/engagements/${e.engagement_id}`} className="hover:text-brass">{e.engagement_name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </PageShell>
  );
}
